'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import WhitePaperCard from '../listing/WhitePaperCard';
import SectionEmptyState from '../listing/SectionEmptyState';
import CategoryFilterChips, {
  buildFilterOptions,
} from '../listing/CategoryFilterChips';
import { useStudyTones } from '../listing/studyTones';
import type { Study, StudyCategorySlug } from 'content/science/studyTypes';
import styles from './ResearchStudiesSection.module.css';

/* Research publications — one section listing every long-form study (white
 * papers + peer-reviewed publications + medRxiv pre-prints + conference
 * posters) in a single card grid. Mirrors the white-papers section it replaced
 * one-for-one: same heading scale, same 3-card grid with the white/grey/dark
 * rotation, same desktop-vs-mobile pagination.
 *
 * The only addition: category filter pills sit directly under the heading
 * instead of a right-aligned dropdown. "All" is selected by default. */

const DESKTOP_INITIAL = 6;
const MOBILE_INITIAL = 3;
/* Three at a time on a phone, not six. A publication card is tall, so six
   arriving at once reads as a page change rather than as "a little more" —
   and it matches the Ask Our Data rail below it, which also steps by three. */
const MOBILE_QUERY = '(max-width: 640px)';

const STEP = 3;

const initialForViewport = (): number => {
  if (typeof window === 'undefined') return DESKTOP_INITIAL;
  return window.matchMedia(MOBILE_QUERY).matches
    ? MOBILE_INITIAL
    : DESKTOP_INITIAL;
};

type Filter = StudyCategorySlug | 'all';

interface Props {
  studies: Study[];
}

const ResearchStudiesSection = ({ studies }: Props) => {
  const t = useTranslations('science');
  const [filter, setFilter] = useState<Filter>('all');
  const [visibleCount, setVisibleCount] = useState(DESKTOP_INITIAL);
  const tones = useStudyTones(studies);

  useEffect(() => {
    setVisibleCount(initialForViewport());
  }, []);

  const filterOptions = useMemo(() => buildFilterOptions(studies), [studies]);

  const filtered = useMemo(() => {
    if (filter === 'all') return studies;
    return studies.filter((s) => s.category === filter);
  }, [filter, studies]);

  const visible = filtered.slice(0, visibleCount);
  const canShowMore = visibleCount < filtered.length;
  const isExpanded = visibleCount > initialForViewport();

  const handleFilter = useCallback((f: Filter) => {
    setFilter(f);
    setVisibleCount(initialForViewport());
  }, []);

  const handleShowMore = useCallback(() => {
    const isMobile =
      typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;
    setVisibleCount((c) => (isMobile ? c + STEP : filtered.length));
  }, [filtered.length]);

  const handleShowLess = useCallback(() => {
    setVisibleCount(initialForViewport());
  }, []);

  const heading = t('science.research.heading');

  if (!studies.length) {
    return (
      <section className={styles.section} aria-label={heading}>
        <div className={styles.inner}>
          <h2 className={styles.heading}>{heading}</h2>
          <SectionEmptyState
            tone="light"
            message={t('science.research.emptyAll')}
          />
        </div>
      </section>
    );
  }

  return (
    <section
      className={styles.section}
      id="research-studies"
      aria-label={heading}
    >
      <div className={styles.inner}>
        <div className={styles.headBlock}>
          <h2 className={styles.heading}>{heading}</h2>
          <div className={styles.filterRow}>
            <CategoryFilterChips
              active={filter}
              options={filterOptions}
              onChange={handleFilter}
              variant="light"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className={styles.empty}>{t('science.research.emptyCategory')}</p>
        ) : (
          <div className={styles.grid}>
            {visible.map((s) => (
              <WhitePaperCard
                key={s.slug}
                study={s}
                variant={tones.get(s.slug)}
              />
            ))}
          </div>
        )}

        {canShowMore ? (
          <div className={styles.buttonWrap}>
            <button
              className={styles.pageBtn}
              type="button"
              onClick={handleShowMore}
            >
              {t('science.research.viewMore')}
              <span className={styles.arrow} aria-hidden="true">
                ↓
              </span>
            </button>
          </div>
        ) : isExpanded ? (
          <div className={styles.buttonWrap}>
            <button
              className={styles.pageBtn}
              type="button"
              onClick={handleShowLess}
            >
              {t('science.research.showLess')}
              <span className={styles.arrow} aria-hidden="true">
                ↑
              </span>
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ResearchStudiesSection;
